import React from 'react';
import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { I18nProvider as LinguiProvider, useLingui } from '@lingui/react';
import i18nIsoCountries, { Alpha2Code } from 'i18n-iso-countries';

export const SUPPORTED_LOCALES = [
  {
    id: 'en',
    label: msg({ id: '_general.locale.en.label', message: 'English' }),
  },
  {
    id: 'fr',
    label: msg({ id: '_general.locale.fr.label', message: 'French' }),
  },
  {
    id: 'ja',
    label: msg({ id: '_general.locale.ja.label', message: 'Japanese' }),
  },
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number]['id'];

/**
 * Locales a wording must always be translated in. Locales added later (e.g. ja)
 * stay optional so organizations are not forced to translate existing content.
 */
export const REQUIRED_LOCALES = ['en', 'fr'] as const satisfies readonly Locale[];

/**
 * Get the browser navigator languages.
 * Some browsers add extra info after a semicolon, e.g. "en-GB;q=0.9", so we strip that out.
 */
export const navigatorLanguages = ((navigator.languages as string[]) ?? ['en-GB']).map(
  (lang) => lang.split(';')[0]
);

/**
 * Get the browser current locale.
 * ⚠️ This function is intended to be used only outside of React components.
 * Otherwise, use the `useDatabaseTranslation` hook.
 */
export function getBrowserLocale() {
  // Locale can be forced in development mode
  if (import.meta.env.VITE_LANGUAGE) {
    return import.meta.env.VITE_LANGUAGE as Locale;
  }

  const userLanguages = navigatorLanguages;

  for (const lang of userLanguages) {
    for (const supportedLocale of SUPPORTED_LOCALES) {
      if (lang.startsWith(supportedLocale.id)) {
        return supportedLocale.id;
      }
    }
  }

  return 'en'; // English by default
}

export type Country = {
  code: Alpha2Code;
  name: string;
};

const loadCountries = async (locale: Locale) => {
  let countriesLocaleData;

  switch (locale) {
    case 'en':
      countriesLocaleData = await import('i18n-iso-countries/langs/en.json');
      break;
    case 'fr':
      countriesLocaleData = await import('i18n-iso-countries/langs/fr.json');
      break;
    case 'ja':
      countriesLocaleData = await import('i18n-iso-countries/langs/ja.json');
      break;
  }

  if (!countriesLocaleData) {
    return [];
  }

  i18nIsoCountries.registerLocale(countriesLocaleData);

  return Object.entries(i18nIsoCountries.getNames(locale))
    .map(([code, name]) => ({
      code: code as Alpha2Code,
      name,
    }))
    .sort((a, b) => (a.name < b.name ? -1 : 1));
};

const countries: Record<string, Country[]> = {};

export async function switchLanguage(locale: Locale) {
  try {
    // The catalog import can reject (missing/404'd chunk) or resolve to
    // null/undefined depending on the bundler; guard both so the locale still
    // activates and renders the default-language source text.
    const { messages } = (await import(`../locales/${locale}.po`).catch(() => null)) ?? {
      messages: {},
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    i18n.load(locale, messages);
    document.documentElement.lang = locale;
    countries[locale] = await loadCountries(locale);

    i18n.activate(locale);
  } catch (err) {
    console.error(err);
  }
}

if (import.meta.env.DEV) {
  // @ts-ignore - Small hack to disable lingui warning in dev mode
  i18n._locale = 'en';
}

// @ts-ignore - Enable to switch language from console
window.switchLanguage = switchLanguage;

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
  return <LinguiProvider i18n={i18n}>{children}</LinguiProvider>;
};

export const useCountries = () => {
  const { i18n } = useLingui();

  return countries[i18n.locale] ?? [];
};

// Required locales are guaranteed on existing wordings; locales added later are optional
export type I18nWording = Record<(typeof REQUIRED_LOCALES)[number], string> &
  Partial<Record<Locale, string | null>>;

export const useDatabaseTranslation = () => {
  const { i18n } = useLingui();
  const currentLocale = i18n.locale as Locale;

  // Wordings stored in database are not guaranteed to contain a non-empty
  // translation for every supported locale, so fall back to English
  const _db = (translation: I18nWording): string => {
    const translated = translation[currentLocale];

    return translated?.trim() ? translated : translation.en;
  };

  return {
    _db,
    _compare: (a: I18nWording, b: I18nWording) => _db(a).localeCompare(_db(b), i18n.locale),
  };
};
